import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Container,
  Section,
  Card,
  buttonStyles,
  fadeUp,
  stagger,
} from "@comai-portal/design-system";
import {
  BlogSeriesFolder,
  BLOG_UNAVAILABLE_MESSAGE,
  BLOG_SERIES_NOT_FOUND_MESSAGE,
  mapApiFolder,
  formatDate,
} from "./blogTypes";
import { blogAPI } from "../../services/api";

// CO 540 — one folder of the blog catalog: the series title and its parts in
// reading order, from the gateway's `/api/blog/series` (the same projection
// `marketing_post_catalog` serves an agent). An unknown key is "not found"; an
// unreachable API is the one honest line — never confused with each other.
type Resolution = "pending" | "found" | "not-found" | "unavailable";

export function BlogSeriesPage() {
  const { key } = useParams<{ key: string }>();
  const [folder, setFolder] = useState<BlogSeriesFolder | undefined>();
  const [resolution, setResolution] = useState<Resolution>(() =>
    key ? "pending" : "not-found",
  );

  useEffect(() => {
    let cancelled = false;
    if (!key) return;

    async function load() {
      try {
        const catalog = await blogAPI.getCatalog();
        if (cancelled) return;
        const hit = catalog.series.find((s) => s.key === key);
        if (hit) {
          setFolder(mapApiFolder(hit));
          setResolution("found");
        } else {
          setResolution("not-found");
        }
      } catch {
        if (!cancelled) setResolution("unavailable");
      }
    }
    load();

    return () => {
      cancelled = true;
    };
  }, [key]);

  if (!folder) {
    if (resolution === "pending") {
      return <div className="bg-paper text-ink dark:bg-ink dark:text-paper min-h-[40vh]" />;
    }
    const unavailable = resolution === "unavailable";
    return (
      <div className="bg-paper text-ink dark:bg-ink dark:text-paper">
        <Section>
          <Container width="prose" className="text-center">
            <h1 className="text-display-md mb-4">
              {unavailable ? "Blog unavailable" : "Series not found"}
            </h1>
            <p
              role="status"
              className="text-lead text-mute dark:text-mute-soft mb-8"
            >
              {unavailable
                ? BLOG_UNAVAILABLE_MESSAGE
                : BLOG_SERIES_NOT_FOUND_MESSAGE}
            </p>
            <Link to="/blog" className={buttonStyles({ variant: "primary" })}>
              Back to blog
            </Link>
          </Container>
        </Section>
      </div>
    );
  }

  return (
    <div className="bg-paper text-ink dark:bg-ink dark:text-paper">
      <Section>
        <Container width="prose">
          <motion.div variants={stagger} initial="hidden" animate="show">
            <motion.div variants={fadeUp}>
              <Link to="/blog" className={buttonStyles({ variant: "ghost" })}>
                ← Back to blog
              </Link>
            </motion.div>
            <motion.p variants={fadeUp} className="ds-eyebrow mt-8">
              Series · {folder.count} {folder.count === 1 ? "part" : "parts"}
            </motion.p>
            <motion.h1
              variants={fadeUp}
              className="text-display-md text-balance mt-3"
            >
              {folder.title}
            </motion.h1>

            <motion.ol
              variants={stagger}
              className="mt-10 grid gap-5 list-none p-0 m-0"
              aria-label={`Parts of ${folder.title}, in reading order`}
            >
              {folder.posts.map((post) => (
                <motion.li key={post.slug} variants={fadeUp}>
                  <Link to={`/blog/${post.slug}`} className="block h-full">
                    <Card hover className="h-full flex flex-col">
                      <div className="flex items-center gap-3 mb-3">
                        <span className="ds-eyebrow">
                          Part {post.series?.part ?? "·"}
                        </span>
                        {post.readingTime != null && (
                          <span className="text-sm text-mute dark:text-mute-soft">
                            {post.readingTime} min read
                          </span>
                        )}
                      </div>
                      <h2 className="text-display-sm mb-2">{post.title}</h2>
                      <p className="text-mute dark:text-mute-soft text-sm flex-grow">
                        {post.excerpt}
                      </p>
                      <p className="text-sm text-mute dark:text-mute-soft mt-4">
                        {post.author.name} · {formatDate(post.publishedAt)}
                      </p>
                    </Card>
                  </Link>
                </motion.li>
              ))}
            </motion.ol>
          </motion.div>
        </Container>
      </Section>
    </div>
  );
}
